/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 5 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl, getImageUrl } from "@Utils";
import { sanitizeHtml } from "@Utils/sanitize";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentProps, ComponentType, RefObject, SyntheticEvent, HTMLAttributes, Key } from "react";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import type { Operators } from "@ts3d-hoops/web-viewer";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { FormValidator } from "@CubeComponents/Mui_Form/FormValidator";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ChipListComponent, {
  ChipsDirective,
  ChipDirective,
} from "@CubeComponents/form/ButtonComponent/ChipListComponent";
import DatePickerComponent from "@CubeComponents/Mui_Form/DatePickerComponent";
import cloneObject from "@Services/cloneObject";
import { cubeView, getCubeViewSnapshot } from "@Services/cubeView";
import { getDefaultListOfStatusAvailable } from "@Features/issues/state/bcfData.actions";
import "./createIssue.css";
import axiosWrapper from "@AxiosWrapper";
import type { SaveIssuesDataResponse } from "@ServerTypes/issues/issues.schema";
import type { GetFormTemplateResponse } from "@ServerTypes/projects/project.schema";
import type { FormTemplateField } from "@ServerTypes/forms/forms.schema";
import type { GetSpaceUsersListResponse, SpaceUserItem } from "@ServerTypes/spaces/space.schema";
import type { ReviewStatusItem } from "@ServerTypes/reviews/reviews.schema";
import { FileManagerAsPopup } from "@Features/files";
import { setAnnotations, setCurrentPagePDF, setModalStatus, setAnimationForIssueCreation } from "@Reducers/Viewer";
import MultiSelectComponent from "@CubeComponents/form/MultiselectComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { Bcf, Color, OperatorId } from "@ts3d-hoops/web-viewer";

import {
  PDF_ENGINE,
  SOMETHING_WENT_WRONG,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_SUCCESS,
  TOAST_TYPE_WARNING,
} from "@Services/constants";
import html2canvas from "html2canvas";
import { getDropdownBackground, getStatusBtnStyle } from "@Features/reviews/helpers";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { showToastNotification } from "@Reducers/toastSlice";
import { formCategories } from "@Helpers";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import ImageEditor from "@Components/ImageEditor";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import IconComponent from "@CubeComponents/icons";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { getFileImage } from "@Services/utils/iconMap";
import { useFormValidator, TEMPLATE_ENTITY_CREATION } from "@Features/forms";
import { useTranslation, untranslated } from "@i18n";
import { uploadStringToTemp } from "@Services/tempUpload";
import { CubeGlobal, HoopsViewerContext, useViewerCallbacks } from "@Features/viewer-core";
import useFileVersionIdAndNodeOffsetIdMap from "@Services/cubeViewHelperHooks";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { Box } from "@mui/material";
import Text from "@CubeComponents/html/Text/Text";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { mergeIssuesData } from "@Features/issues/state/issues";
import { mergeIssuesPinData } from "@Features/issues/state/issuesPinDataSlice";
import { getSpaceUsersList } from "@Services/dataManager";
import { RootState } from "@Reducers/index";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import AddAssignedToUsersList from "./AddAssignedToUsersList";

// Selected-status dropdown state. `background` comes from getDropdownBackground (CSS value).
interface StatusState {
  _id: string | null | undefined;
  background: unknown;
}

// Details passed when creating an issue from a clash record (bypasses form validation).
interface ClashDetails {
  status: string | undefined;
  clashName?: string;
}

// Attached-file shape held in `issueAttachedFiles` local state and rendered as chips.
interface AttachedFileItem {
  name?: string;
  fileVersionId?: string;
  resourceType?: string;
  fileId?: string;
}

// File entry returned by the FileManager popup selection callback.
interface SelectedFile {
  name?: string;
  fileVersion?: string;
  resourceType?: string;
  hierarchyId?: string;
}

/**
 * The clash-results tab's handle onto this form. Described structurally rather than imported: the
 * types live in `components/ClashTests/Details/Results`, are not exported, and only the three
 * members read here matter. Both callbacks use METHOD syntax so their parameters stay bivariant —
 * the real handlers declare narrower payloads (`ClashResultRow`) that `strictFunctionTypes` would
 * otherwise reject at the JSX boundary.
 */
interface ClashIssueData {
  handleClickCollisionOutput(args: { data: unknown[]; offSet: Record<string, number> }): unknown;
  treeGrid: RefObject<{
    slickGrid: { getSelectedRows(): number[]; getDataItem(row: number): unknown };
  } | null>;
  formTemplate?: { template?: unknown };
}

/** In-flight state of the bulk "one issue per selected clash" loop driven by `triggerClashIssue`. */
interface ClashIssueProgress {
  total: number;
  progress: number;
  issueStatus: string | undefined;
  offSet: Record<string, number>;
  selectedRecords: unknown[];
}

/**
 * The custom pin markup as the viewer hands it to `measurementCreated`. `_name` is `protected` on
 * `MeasureMarkup` and `setPinColor` is declared on the `CustomPinMarkup` class expression in
 * viewer-core (reached through the untyped `CubeGlobal`), so both are named by an erased
 * intersection over the shipped markup type at each read.
 */
interface CustomPinMarkupMembers {
  _name: string;
  setPinColor(color: Color): void;
}

/** The one member `findMarkupByGuid` matches live markups on. */
interface MarkupWithCubeUniqueId {
  cubeUniqueId: string;
}

/**
 * DEAD CODE, preserved verbatim: `_handleStatusChange` / `_handleSeverityChange` are referenced
 * nowhere — they are the Syncfusion dropdown handlers left behind by the MUI migration. This is the
 * slice of the Syncfusion change event they read.
 */
interface DropdownChangeEvent {
  target: { isInteracted?: boolean; value: string; itemData: { category?: string } };
}

/** The `li` props MUI hands to `renderOption` (it injects a `key` alongside the DOM attributes). */
type AutoCompleteOptionProps = HTMLAttributes<HTMLLIElement> & { key: Key };

/**
 * The create/update payload this form assembles. Every field past the identity ones is optional
 * because the branches add them conditionally (viewer vs PDF) and the `*Key` fields replace their
 * inline counterparts once the blobs are uploaded to temp storage. The index signature carries
 * `extraParametersOnIssueCreation`, which the standalone-grid caller spreads in wholesale.
 */
interface SavedIssueData {
  [key: string]: unknown;
  fileId?: string;
  topicId: string;
  attachedReviewId?: string | null;
  topicText?: string;
  description: string;
  status?: string | null;
  assignedTo: string[];
  dueDate?: number | null;
  spaceId?: string;
  attachedFiles: (string | undefined)[];
  tagsData: string[];
  formTemplate?: unknown;
  severity?: string | null;
  formId: string[];
  id?: string;
  viewpoint?: string;
  bcfData?: string;
  snapshot?: string;
  pinData?: string;
  pdfCurrentPage?: number;
  zoomLevel?: number;
  xScrollPosition?: number;
  yScrollPosition?: number;
  snapshotKey?: string;
  viewpointKey?: string;
  bcfDataKey?: string;
}

interface CreateIssueProps {
  /** Review the created issue is attached to; `CreateIssueModal` forwards it as `string | null`. */
  attachedReviewId?: string | null;
  /** Only set by the clash-results tab, which drives the bulk clash-issue loop. */
  clashIssueData?: ClashIssueData;
  closeAddIssueModal?: () => void;
  engineType?: string;
  /** Extra request fields the standalone grid injects; spread verbatim into the payload. */
  extraParametersOnIssueCreation?: Record<string, unknown>;
  fileVersionId?: string;
  /** DEAD PROP: `IssuesPallet` passes it, nothing here reads it. */
  fileType?: string;
  isEditMode?: boolean;
  /** The form renders outside the 3D/PDF viewer: manual image, no snapshot/viewpoint/pin capture. */
  isOutsideViewer?: boolean;
  onCancel?: () => void;
  refreshDataGrid?: () => void;
  setTriggerClashIssue?: (value: boolean) => void;
  transmittalVersionId?: string | null;
  triggerClashIssue?: boolean;
  /** Space the issue is created in; `CreateIssueModal`'s space dropdown supplies `string | null`. */
  workspaceId?: string | null;
}

function CreateIssue(props: Readonly<CreateIssueProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const statusDropdownRef = useRef<HTMLDivElement | null>(null);
  const steverityDropdownRef = useRef<HTMLDivElement | null>(null);
  const { viewerImpl, isModelStructureReady, isSettingsApplied } = useContext(HoopsViewerContext);
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const { markupAnnotations: annotationData, currentPageInfo } = useSelector((state: RootState) => state.viewerState);
  const projectData = useSelector((state: RootState) => state.projectData);
  const editIssues = useSelector((state: RootState) => state.editIssues);
  const spacesUsersData = useSelector((state: RootState) => state.spacesUsersData);
  const filesData = useSelector((state: RootState) => state.filesData);
  const transmittals = useSelector((state: RootState) => state.transmittals);

  const isEditIssue = props.isEditMode || HoopsModalStatus?.Issues?.currentStage === "edit_issue";
  const isFileBasedEditIssue = isEditIssue && Boolean(editIssues?.fileVersionId);

  const [issueTitle, setIssueTitle] = useState("");
  const [issueAttachedFiles, setIssueAttachedFiles] = useState<AttachedFileItem[]>([]);
  const [issueDescription, setIssueDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [createButton, setCreateButton] = useState("Create");
  const [spaceId, setSpaceId] = useState<string | undefined>();
  const [pinData, setPinData] = useState<Record<string, unknown>>({});
  const [pinStatus, setPinStatus] = useState("NOT_STARTED");
  const [showFileManager, setShowFileManager] = useState(false);
  const [tagsDataSource, setTagsDataSource] = useState(["Severe", "Low", "Important"]);
  const [initialCustomTags, setInitialCustomTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState<string[]>([]);
  const [actionButtons, setactionButtons] = useState(false);
  const [dueDate, setDueDate] = useState<number | null | undefined>(null);
  // `engineType` is optional, and `includes(undefined) === false` is the intent here, so the array
  // is widened rather than the argument coerced.
  const [pdfRender] = useState(([PDF_ENGINE] as (string | undefined)[]).includes(props.engineType));
  const [Status, setStatus] = useState<StatusState>({
    _id: null,
    background: null,
  });
  const [formTemplate, setFormTemplate] = useState<NonNullable<GetFormTemplateResponse["data"]>>(
    {} as NonNullable<GetFormTemplateResponse["data"]>,
  );
  const [severity, setSeverity] = useState<string | null | undefined>(null);
  // _query removed — was unused dead code from Syncfusion Query migration
  const [clashIssueProgress, setClashIssueProgress] = useState<ClashIssueProgress | null>(null);
  const [statusOptions, setStatusOptions] = useState<ReviewStatusItem[]>([]);
  const [spaceUsers, setSpaceUsers] = useState<SpaceUserItem[]>([]);
  const [cancelClashIssue, setCancelClashIssue] = useState(false);
  const params = useParams() as { fileId: string };
  const [imageForStandaloneIssue, setImageForStandaloneIssue] = useState("");
  const [standaloneImageEdited, setStandaloneImageEdited] = useState(false);
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const formId: string[] = [];

  const offSet = useFileVersionIdAndNodeOffsetIdMap(params.fileId);

  const fileVersionId = props.fileVersionId;
  const formObject = useRef<FormValidator | null>(null);
  const formValidator = useFormValidator({});

  const severityTypes = [
    {
      id: "MINOR",
      text: t("Minor"),
    },
    {
      id: "MAJOR",
      text: t("Major"),
    },
  ];

  useEffect(() => {
    if (
      getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) &&
      !isEditIssue
    ) {
      const getAllMeasurements = viewerImpl?.hwv.measureManager.getAllMeasurements();
      for (const markup of getAllMeasurements) {
        viewerImpl?.hwv.measureManager.removeMeasurement(markup);
      }
    }
  }, []);

  useEffect(() => {
    const options = {
      rules: {
        title: {
          required: [true, t("Title is required")],
        },
        status: {
          required: [true, t("Status is required")],
        },
      },
    };
    formObject.current = new FormValidator("#createIssueForm", options);
  }, []);

  // The four aliases below keep their original identifiers: renaming a local const perturbs the
  // minifier's name assignment, so the now-stale `Any` suffix is left for a separate pass.
  // `ChipDirective`, `MultiSelectComponent` and `FileManagerAsPopup` already declare (or index-sign)
  // every prop passed below, so their casts are simply dropped.
  const ChipDirectiveAny = ChipDirective;
  const MultiSelectComponentAny = MultiSelectComponent;
  // `projectId` is not on `FormPreviewProps`; the alias names just that extra prop.
  const FormPreviewAny = FormPreview as ComponentType<ComponentProps<typeof FormPreview> & { projectId?: string }>;
  const FileManagerAsPopupAny = FileManagerAsPopup;

  useEffect(() => {
    if (projectData._id && !isEditIssue) {
      axiosWrapper
        .post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
          projectId: projectData._id,
          resourceType: formCategories.issues,
        })
        .then(({ data }) => {
          setFormTemplate((data.data || {}) as NonNullable<GetFormTemplateResponse["data"]>);
        });
    }
  }, [projectData._id, isEditIssue]);

  // Hoisted so the effect below can depend on this ONE entry rather than on the whole `filesData`
  // map, whose identity changes on every write to any file.
  const currentFileData = fileVersionId ? filesData[fileVersionId] : undefined;

  useEffect(() => {
    if (props.transmittalVersionId) {
      // MISMATCH, preserved verbatim: the slice declares `state.transmittals` as ONE transmittal's
      // data (or `{}`), but this effect reads it as a map keyed by transmittal version id. One of
      // the two is wrong; asserted locally rather than changing either.
      if (
        transmittals &&
        (transmittals as typeof transmittals & Record<string, { TRANSMITTAL_TOSPACEIDS: { value: string } }>)[
          props.transmittalVersionId
        ]
      ) {
        setSpaceId(
          (transmittals as typeof transmittals & Record<string, { TRANSMITTAL_TOSPACEIDS: { value: string } }>)[
            props.transmittalVersionId
          ]!.TRANSMITTAL_TOSPACEIDS.value,
        );
      }
    } else if (fileVersionId) {
      if (currentFileData) {
        setSpaceId(currentFileData.spaceId);
      }
    }
  }, [fileVersionId, currentFileData, transmittals, spacesUsersData, props.transmittalVersionId]);

  useEffect(() => {
    const fetchStatusOptions = async () => {
      if (spaceId) {
        const response = await getDefaultListOfStatusAvailable({ spaceId, entityType: "ISSUES" });
        if (response.data?.result) {
          setStatusOptions(response.data.result);
        }
      }
    };
    fetchStatusOptions();
  }, [spaceId]);

  useEffect(() => {
    const fetchSpaceUsers = async () => {
      if (spaceId) {
        const response = await axiosWrapper.post<GetSpaceUsersListResponse>(getSpaceUsersList, { value: { spaceId } });
        if (response.data?.result) {
          setSpaceUsers(response.data.result);
        }
      }
    };
    fetchSpaceUsers();
  }, [spaceId]);

  useEffect(() => {
    if (props.workspaceId) {
      setSpaceId(props.workspaceId);
      setAssignedTo([]);
    }
  }, [props.workspaceId]);

  function dropdownItemTemplate(args: ReviewStatusItem) {
    const style = getStatusBtnStyle(args.category);

    return (
      <div className="px-3 py-2">
        <Text style={style} className="rounded px-4 py-1 text-sm text-white">
          {args.name}
        </Text>
      </div>
    );
  }

  const validateData = () => {
    let formValidated = formObject.current?.validate();
    if (formValidator.validateAll) {
      formValidated = formValidator.validateAll() && formValidated;
    }
    return formValidated;
  };

  const handleCreateIssue = async ({ clashDetails }: { clashDetails?: ClashDetails } = {}) => {
    if (clashDetails || validateData()) {
      if (!isEditIssue && !(Status._id || clashDetails?.status)) {
        dispatch(
          showToastNotification({
            content: t("Please specify status of the issue"),
            type: TOAST_TYPE_WARNING,
          }),
        );
        return;
      }

      setactionButtons(true);

      let savedData: SavedIssueData = {
        fileId: fileVersionId,
        topicId: "dfsf",
        attachedReviewId: props.attachedReviewId,
        topicText: clashDetails ? clashDetails.clashName : issueTitle,
        description: issueDescription,
        status: isEditIssue ? undefined : clashDetails ? clashDetails.status : Status._id,
        assignedTo,
        dueDate,
        spaceId,
        attachedFiles: issueAttachedFiles.map((data) => data.fileVersionId),
        tagsData: customTag,
        // `clashDetails` is only ever supplied by the clash loop, which is the same branch that
        // supplies `clashIssueData`; erased assertion rather than a guard that would emit.
        formTemplate: clashDetails ? props.clashIssueData!.formTemplate?.template : formTemplate?.template,
        severity,
        formId,
        ...props.extraParametersOnIssueCreation,
      };

      if (!pdfRender) {
        let viewpoint = "";
        let bcfData = "";
        let snapshot = standaloneImageEdited ? imageForStandaloneIssue : "";
        if (!props.isOutsideViewer) {
          // KNOWN BUG, preserved verbatim: `handleIssueViewPoint` returns `undefined` when the
          // viewer has no `hwv`, and the result is indexed without a guard. Erased assertions.
          const viewPoints = await handleIssueViewPoint();
          snapshot = await handleTakeSnapshot();
          viewpoint = JSON.stringify(viewPoints![1]);
          bcfData = JSON.stringify(viewPoints![0]);
        }
        savedData = {
          ...savedData,
          viewpoint,
          bcfData,
          snapshot,
          pinData: JSON.stringify(pinData),
        };
      } else {
        const pdfContainer = document.getElementById("pdfContainer");
        if (pdfContainer) {
          const canvas = await html2canvas(pdfContainer);
          savedData.snapshot = canvas.toDataURL("base64");
        }
        savedData.viewpoint = JSON.stringify(annotationData);
        savedData.pdfCurrentPage = currentPageInfo.pageNumber;
        savedData.zoomLevel = currentPageInfo.zoomLevel;
        savedData.xScrollPosition = currentPageInfo.xScrollPosition;
        savedData.yScrollPosition = currentPageInfo.yScrollPosition;
      }

      if (isEditIssue && editIssues) {
        savedData.id = editIssues.id;
      }

      const shouldUploadSnapshot = !!savedData.snapshot && savedData.snapshot.startsWith("data:");
      const [snapshotKey, viewpointKey, bcfDataKey] = await Promise.all([
        shouldUploadSnapshot
          ? // `shouldUploadSnapshot` already required `savedData.snapshot` to be a non-empty string,
            // but it is a separate boolean so the narrowing does not carry. Erased assertion.
            uploadStringToTemp(savedData.snapshot!, "issue-snapshot.txt")
          : Promise.resolve(undefined),
        savedData.viewpoint
          ? uploadStringToTemp(savedData.viewpoint, "issue-viewpoint.json")
          : Promise.resolve(undefined),
        savedData.bcfData ? uploadStringToTemp(savedData.bcfData, "issue-bcf.json") : Promise.resolve(undefined),
      ]);

      if (
        (shouldUploadSnapshot && !snapshotKey) ||
        (savedData.viewpoint && !viewpointKey) ||
        (savedData.bcfData && !bcfDataKey)
      ) {
        dispatch(
          showToastNotification({
            content: SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
        setactionButtons(false);
        return;
      }

      if (snapshotKey) {
        savedData.snapshotKey = snapshotKey;
        delete savedData.snapshot;
      }

      if (viewpointKey) {
        savedData.viewpointKey = viewpointKey;
        delete savedData.viewpoint;
      }

      if (bcfDataKey) {
        savedData.bcfDataKey = bcfDataKey;
        delete savedData.bcfData;
      }

      const response = await axiosWrapper.post<SaveIssuesDataResponse>(
        `${getBackendUrl()}/issues/${savedData.id ? "updateIssue" : "createIssue"}`,
        savedData,
      );
      if (response.data.status) {
        const responseData = response.data.data!;
        dispatch(mergeIssuesData({ data: responseData, editId: savedData.id }));
        dispatch(
          mergeIssuesPinData({
            id: responseData.id!,
            pinData: responseData.pinData ?? undefined,
          }),
        );
        const toastContent = savedData.id
          ? "Issue has been edited successfully!"
          : "Issue has been created successfully!";
        dispatch(
          showToastNotification({
            content: untranslated(toastContent),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      const status = response.data.status;
      setactionButtons(false);
      if (status) {
        if (props.closeAddIssueModal) {
          props.closeAddIssueModal();
        }
        if (props.refreshDataGrid) {
          props.refreshDataGrid();
        }
        handleCancelIssue();
        dispatch(setAnimationForIssueCreation(true));
        setTimeout(() => {
          dispatch(setAnimationForIssueCreation(false));
        }, 1500);
      }
    } else {
      dispatch(
        showToastNotification({
          content: t("Please fill all mandatory fields"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  const handleCancelIssue = () => {
    if (props.onCancel) {
      props.onCancel();
    }
    dispatch(
      setModalStatus({
        ...HoopsModalStatus,
        Issues: {
          ...HoopsModalStatus.Issues,
          currentStage: HoopsModalStatus.Issues?.defaultStage,
          backStage: HoopsModalStatus.Issues?.defaultStage,
        },
      }),
    );
  };

  const handleCustomFieldsData = (data: FormTemplateField[]) => {
    setFormTemplate((prev) => ({ ...prev, template: data }));
  };

  const handleIssueViewPoint = async () => {
    if (!CubeGlobal.hwv) return;
    // Held as a plain `object`, not as `Bcf.BCFViewpoint`: the back-reference deleted on the next
    // line is `private` on the shipped class, and an intersection naming a private member collapses
    // to `never`, so widening the declaration is the only erased way to reach it. `cloneObject` also
    // returns a bare object, which is assigned back into the same variable.
    let bcfData: object = await Bcf.BCFViewpoint.createViewpoint(CubeGlobal.hwv, "viewpoint.bcfv");
    delete (bcfData as { _viewer?: unknown })._viewer;
    bcfData = cloneObject(bcfData);

    const viewpoint = await cubeView.toJson(viewerImpl);
    return [bcfData, viewpoint];
  };

  const handleTakeSnapshot = async () => await getCubeViewSnapshot();

  useEffect(() => {
    const viewerContainer = document.getElementById("viewerContainer");
    if (viewerContainer && !props.clashIssueData) {
      setTimeout(() => {
        viewerContainer.classList.add("animate-flash");
        setTimeout(() => {
          viewerContainer.classList.remove("animate-flash");
        }, 1000);
      }, 300);
    }
  }, []);

  const handleData = async () => {
    if (isModelStructureReady || pdfRender || props.isOutsideViewer) {
      if (isEditIssue) {
        if (!editIssues) return;
        const attachedFiles: AttachedFileItem[] = [];
        if (editIssues.attachedFiles?.length) {
          editIssues.attachedFiles.map((file) => {
            attachedFiles.push({
              name: file.originalName,
              fileVersionId: file.fileVersionId,
              resourceType: file.resourceType,
            });
          });
        }
        setIssueTitle((editIssues.title ?? "").trim());
        setSeverity(editIssues.severity);
        setIssueDescription(sanitizeHtml(editIssues.description ?? ""));
        setAssignedTo(Array.isArray(editIssues.assignedTo) ? editIssues.assignedTo.map((u) => u._id) : []);
        setDueDate(editIssues.dueDate);
        if (editIssues.formTemplate) {
          setFormTemplate((prev) => ({
            ...prev,
            template: editIssues.formTemplate,
          }));
        }
        if (editIssues && props.isOutsideViewer && editIssues.snapshot) {
          setImageForStandaloneIssue(getUrlForImage({ id: editIssues.id }, "IssueImage"));
        }
        setIssueAttachedFiles(attachedFiles);
        const tagList = editIssues.tagData ? editIssues.tagData.split(",") : [];
        setTagsDataSource([...tagsDataSource, ...tagList]);

        const pinObj = editIssues.pinData ? JSON.parse(editIssues.pinData) : {};
        if (viewerImpl) {
          const markupToHighlight = findMarkupByGuid({
            guid: pinObj._uniqueId,
          });
          markupToHighlight?.[0]?.onSelect();
        }
        setPinData(pinObj);
        if (editIssues.tagData) {
          setInitialCustomTags(editIssues.tagData.split(","));
          setCustomTag(editIssues.tagData.split(","));
        }
        const viewPointValue = editIssues.viewpoint;
        const pdfIssuePage = editIssues.pdfCurrentPage;
        const pdfZoomLevel = editIssues.zoomLevel;
        const xScrollPosition = editIssues.xScrollPosition;
        const yScrollPosition = editIssues.yScrollPosition;

        if (pdfRender && viewPointValue) {
          dispatch(
            setCurrentPagePDF({
              pageNumber: pdfIssuePage ?? 1,
              reason: "viewIssue",
              zoomLevel: pdfZoomLevel ?? 1,
              xScrollPosition: xScrollPosition ?? 0,
              yScrollPosition: yScrollPosition ?? 0,
            }),
          );
          dispatch(setAnnotations({ data: JSON.parse(viewPointValue), annotationType: "markupAnnotations" }));
        }
      }
    }
  };
  useEffect(() => {
    handleData();
  }, [isModelStructureReady, editIssues]);

  useEffect(() => {
    if (props.triggerClashIssue) {
      initializeClashIssue();
    }
  }, [props.triggerClashIssue]);

  async function initializeClashIssue() {
    // This runs only under `props.triggerClashIssue`, which only the clash-results tab sets, and it
    // mounts the grid alongside. Erased assertions; guards here would change behaviour.
    const index = props.clashIssueData!.treeGrid.current!.slickGrid.getSelectedRows();
    const selectedRecords: unknown[] = [];
    for (const idx of index) {
      selectedRecords.push(props.clashIssueData!.treeGrid.current!.slickGrid.getDataItem(idx));
    }
    const statusData = await getDefaultListOfStatusAvailable({
      spaceId: spaceId!,
      entityType: "ISSUES",
    });
    let issueStatus;
    statusData.data.result?.map((data) => {
      if (data.name == "In Review") {
        issueStatus = data._id;
      }
    });
    setClashIssueProgress({
      total: selectedRecords.length,
      progress: 0,
      issueStatus,
      offSet,
      selectedRecords,
    });
  }

  useEffect(() => {
    if (clashIssueProgress) {
      createClashIssue();
    }
  }, [clashIssueProgress]);

  // Only ever called from the effect above, which has already checked `clashIssueProgress`, and only
  // on the clash-results path, which supplies `clashIssueData` / `setTriggerClashIssue`. Erased
  // assertions throughout; guards would change behaviour.
  const createClashIssue = async () => {
    const selectedRecords = clashIssueProgress!.selectedRecords;
    const i = clashIssueProgress!.progress;
    if (cancelClashIssue || i == selectedRecords.length) {
      props.setTriggerClashIssue!(false);
      setClashIssueProgress(null);
      setCancelClashIssue(false);
      return;
    }

    const record = selectedRecords[i];
    await props.clashIssueData!.handleClickCollisionOutput({
      data: [record],
      offSet: clashIssueProgress!.offSet,
    });

    await handleCreateIssue({
      clashDetails: {
        status: clashIssueProgress!.issueStatus,
        // The grid rows are opaque here (see `ClashIssueData`); only the clash name is read.
        clashName: (record as { clashName?: string }).clashName,
      },
    });

    setClashIssueProgress({
      ...clashIssueProgress!,
      progress: i + 1,
    });
  };

  useEffect(() => {
    if (isEditIssue) {
      setCreateButton("Save");
    } else {
      setCreateButton("Create");
    }
  }, [isEditIssue]);

  const _handleStatusChange = (event: DropdownChangeEvent) => {
    if (event.target.isInteracted) {
      const _id = event.target.value;
      const { itemData } = event.target;
      const background = getDropdownBackground(itemData.category);

      setStatus({ _id, background });
    }
  };

  const _handleSeverityChange = (event: DropdownChangeEvent) => {
    const id = event.target.value;
    setSeverity(id);
  };

  // `DatePickerComponent` calls back with `{ value: Date | null, target: { value } }`.
  const handleDueDateChange = (e: { value: Date | null }) => {
    if (e.value) {
      // KNOWN BUG, preserved verbatim: `Date.parse` takes a STRING; it is handed a `Date`, which
      // only works because JS coerces it via `toString()` (losing sub-second precision). Erased
      // assertion; `e.value.getTime()` would be the fix but changes emit.
      setDueDate(Date.parse(e.value as typeof e.value & string));
    } else {
      setDueDate(null);
    }
  };

  const viewerCallbacks = {
    measurementCreated: (measurement: Operators.Markup.Measure.MeasureMarkup) => {
      const { _name } = measurement as typeof measurement & CustomPinMarkupMembers;
      if (_name === "CustomPinMarkup") {
        setPinStatus(() => "NOT_STARTED");
        (measurement as typeof measurement & CustomPinMarkupMembers).setPinColor(new Color(255, 0, 0));
        viewerImpl?.activateOperator(OperatorId.Select);
        // `MarkupItem.toJson()` is declared as returning bare `object`; the pin's own override
        // returns the serialised pin fields this state holds.
        setPinData(() => measurement.toJson() as Record<string, unknown>);
      }
    },
  };

  useViewerCallbacks({ viewerCallbacks });

  const handleAddPin = () => {
    if (pinStatus == "NOT_STARTED") {
      setPinStatus("PENDING");
      viewerImpl?.hwv.operatorManager.set(CubeGlobal.OperatorId.CustomPinOperatorID, 1);
    } else {
      setPinStatus("NOT_STARTED");
      viewerImpl?.activateOperator(OperatorId.Select);
    }
  };

  const findMarkupByGuid = ({ guid }: { guid: unknown }) => {
    const getAllMeasurements = viewerImpl?.hwv.measureManager.getAllMeasurements();
    return getAllMeasurements.filter((record: MarkupWithCubeUniqueId) => record.cubeUniqueId == guid);
  };

  const handleRemovePin = () => {
    const markupsToRemove = findMarkupByGuid({
      guid: pinData._uniqueId,
    });
    for (const markup of markupsToRemove) {
      viewerImpl?.hwv.measureManager.removeMeasurement(markup);
    }
    setPinData({});
  };

  const handleAddFile = () => {
    setShowFileManager(true);
  };

  const handleFileSelectedButtonClick = (params: Record<string, SelectedFile>) => {
    const newData: AttachedFileItem[] = [...issueAttachedFiles];
    for (const file of Object.values(params)) {
      if (!newData.filter((a) => a.fileVersionId === file.fileVersion).length) {
        newData.push({
          name: file.name,
          fileVersionId: file.fileVersion,
          resourceType: file.resourceType,
          fileId: file.hierarchyId,
        });
      }
    }
    setIssueAttachedFiles(newData);
    setShowFileManager(false);
  };
  const handleFileCancelButtonClick = () => {
    setShowFileManager(false);
  };

  const handleRemoveAttachFile = (params: { data: { fileVersionId?: string; [key: string]: unknown } }) => {
    const { fileVersionId } = params.data;
    const tempAttachFiles = issueAttachedFiles.filter((data) => {
      if (data.fileVersionId != fileVersionId) {
        return true;
      }
      return false;
    });
    setIssueAttachedFiles(tempAttachFiles);
  };

  const handleAddPreFilledTag = (args: { itemData: string }) => {
    setCustomTag((prev) => [...prev, args.itemData]);
  };

  const handleRemoveCustomTag = (args: { itemData: string }) => {
    setCustomTag((prev) => prev.filter((tag) => tag != args.itemData.trim()));
  };

  const onSubmitFunction = async (base64EncodedImg: string) => {
    setImageForStandaloneIssue(base64EncodedImg);
    setStandaloneImageEdited(true);
  };

  const handleDeleteImage = async () => {
    setImageForStandaloneIssue("");
  };

  const statusDropdown = useCallback(
    () => (
      <AutoComplete
        ref={statusDropdownRef}
        options={statusOptions}
        valueField="_id"
        getOptionLabel={(option: ReviewStatusItem) => option?.name || ""}
        value={Status?._id}
        onChange={(_event: SyntheticEvent, newValue: ReviewStatusItem | null) => {
          if (newValue) {
            const background = getDropdownBackground(newValue.category);
            setStatus({ _id: newValue._id, background });
          }
        }}
        renderOption={(props: AutoCompleteOptionProps, option: ReviewStatusItem) => (
          <li {...props} key={option._id}>
            {dropdownItemTemplate(option)}
          </li>
        )}
        disabled={!spaceId}
        renderInput={(params: AutocompleteRenderInputParams) => <TextFieldComponent {...params} name="status" />}
      />
    ),
    [Status?._id, spaceId, statusOptions],
  );

  const severityDropdown = () => (
    <AutoComplete
      ref={steverityDropdownRef}
      options={severityTypes}
      valueField="id"
      getOptionLabel={(option: (typeof severityTypes)[number]) => option?.text || ""}
      value={severity}
      onChange={(_event: SyntheticEvent, newValue: (typeof severityTypes)[number] | null) => {
        setSeverity(newValue?.id);
      }}
      renderInput={(params: AutocompleteRenderInputParams) => <TextFieldComponent {...params} />}
    />
  );

  const removeUserFromList = useCallback((_id: string) => {
    setAssignedTo((prev) => prev.filter((assignedTo) => assignedTo != _id));
  }, []);

  const initiateAddUser = () => {
    setOpenAddUserModal(true);
  };

  const formatUserTag = useCallback(
    (_id: string) => {
      return <UserNameTagComponent id={_id} handleDeleteIconClicked={() => removeUserFromList(_id)} />;
    },
    [removeUserFromList],
  );

  const assignedToUsers = useMemo(
    () => (
      <div className="flex-1">
        <div className="flex flex-wrap items-center justify-start gap-2">
          <ButtonComponent
            className="flex items-center text-center"
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={initiateAddUser}
          >
            <span className="flex h-full items-center py-1">
              <IconComponent name="User Profile" width="1.25rem" height="1.25rem" />
            </span>
          </ButtonComponent>
          {assignedTo.map((item, _index: number) => formatUserTag(item))}
        </div>
      </div>
    ),
    [assignedTo, formatUserTag],
  );

  const handleAddUser = (selectedUsers: SpaceUserItem[]) => {
    setAssignedTo((prev) => [...prev, ...selectedUsers.map((user) => user.userId)]);
  };

  return (
    <>
      <div className="flex flex-col h-full box-border py-6" id="createIssueForm">
        <div
          className={`overflow-auto px-6 ${
            props.isOutsideViewer ? "mx-6 rounded-md border border-border_light py-4" : ""
          }`}
          data-testid="createIssueBody"
        >
          {openAddUserModal && (
            <AddAssignedToUsersList
              closeModal={() => setOpenAddUserModal(false)}
              users={spaceUsers}
              excludedUsers={assignedTo}
              handleNext={handleAddUser}
            />
          )}
          {props.isOutsideViewer && (
            <div className="flex flex-col mb-8">
              <label className="text-sm pb-4 font-semibold" data-testid="imageTextLabel">
                <Text>{t("Image")}</Text>
              </label>
              <div
                className="w-74.96 overflow-hidden rounded-md border border-border_light p-2"
                data-testid="projectImageWrapper"
              >
                <ImageEditor
                  isEditable={!isFileBasedEditIssue}
                  imageData={imageForStandaloneIssue}
                  onSubmitFunction={onSubmitFunction}
                  heading={t("Add Issue Image")}
                  onDelete={handleDeleteImage}
                  defaultImageData={`${getImageUrl()}/Issues_default_image.svg`}
                />
              </div>
            </div>
          )}
          <div className="flex flex-col mb-8" data-testid="titleCreateIssue">
            <div className="flex">
              <label className="text-sm pb-4 font-semibold" data-testid="titleTextLabel">
                <Text>{t("Title")}</Text>
              </label>
              <span className="text-2xl align-middle font-light text-red-500">*</span>
            </div>
            <TextFieldComponent
              type="text"
              name="title"
              value={issueTitle}
              slotProps={{ htmlInput: { maxLength: 256 } }}
              onChange={(e) => setIssueTitle(e.target.value)}
            />
          </div>
          <div className="flex flex-col mb-8" data-testid="descCreateIssue">
            <label className="text-sm pb-4 font-semibold" data-testid="descTextLabel">
              <Text>{t("Description")}</Text>
            </label>
            <SimpleEditor value={issueDescription} onChange={(value) => setIssueDescription(value)} />
          </div>

          {!isEditIssue && (
            <div className="flex flex-col mb-8" data-testid="statusCreateIssue">
              <div className="flex">
                <label className="text-sm pb-4 font-semibold" data-testid="statusTextLabel">
                  <Text>{t("Status")}</Text>
                </label>
                <span className="text-2xl align-middle font-light text-red-500">*</span>
              </div>
              {statusDropdown()}
            </div>
          )}

          <div className="flex flex-col mb-8" data-testid="severityCreateIssue">
            <label className="text-sm pb-4 font-semibold" data-testid="severityTextLabel">
              <Text>{t("Severity")}</Text>
            </label>
            {severityDropdown()}
          </div>

          <div className="flex flex-col mb-8" data-testid="dueDateCreateIssue">
            <label className="text-sm pb-4 font-semibold" data-testid="dueDateTextLabel">
              <Text>{t("Due Date")}</Text>
            </label>
            <DatePickerComponent
              value={dueDate && new Date(Number(dueDate))}
              onChange={handleDueDateChange}
              format="dd-MMM-yy"
            />
          </div>

          {!pdfRender && !props.isOutsideViewer && (
            <div className="mb-[1.875rem] flex items-center justify-between" data-testid="pinCreateIssue">
              <span className="text-sm font-bold text-[#192048]" data-testid="pinTextLabel">
                <Text>{t("Pin")}</Text>
              </span>
              {!Object.keys(pinData).length && (
                <ButtonComponent onClick={handleAddPin} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
                  {pinStatus == "NOT_STARTED" ? t("Add pin") : t("Cancel pin")}
                </ButtonComponent>
              )}
              {Object.keys(pinData).length ? (
                <div className="flex">
                  <ChipListComponent id="chip-pin" enableDelete delete={handleRemovePin}>
                    <ChipsDirective>
                      <ChipDirective text={t("Pin")} />
                    </ChipsDirective>
                  </ChipListComponent>
                </div>
              ) : null}
            </div>
          )}

          <div className="flex gap-6 mb-8" data-testid="assignedToCreateIssue">
            <div className="text-sm font-semibold" data-testid="assignedToTextLabel">
              <Text>{t("Assigned to")}</Text>
            </div>
            {assignedToUsers}
          </div>

          <div className="flex flex-col mb-8" data-testid="attachmentsCreateIssue">
            <label className="text-sm pb-4 font-semibold" data-testid="attachmentsTextLabel">
              <Text>{t("Attachments")}</Text>
            </label>
            <Box
              onClick={handleAddFile}
              className="flex h-8 cursor-pointer items-center border border-[rgb(25_32_72_/50%)]"
              data-testid="handleAddFile"
            >
              <div
                className="flex w-full justify-between px-4 text-sm text-[#192048]"
                data-testid="attachFileBodyContent"
              >
                <Text data-testid="attachFileText">{t("Attach File")}</Text>
                <IconComponent name="attachFile" width="1.25rem" height="1.25rem" />
              </div>
            </Box>

            <div className="attachedFilesList" data-testid="attachFileList">
              <ChipListComponent id="chip-attach-files" enableDelete delete={handleRemoveAttachFile}>
                <ChipsDirective>
                  {issueAttachedFiles?.map((data) => {
                    const loadingUrl = getFileImage(data);
                    return (
                      <ChipDirectiveAny
                        key={data.fileVersionId}
                        fileVersionId={data.fileVersionId}
                        text={data.name}
                        leadingIconUrl={loadingUrl}
                      />
                    );
                  })}
                </ChipsDirective>
              </ChipListComponent>
            </div>
          </div>

          <div className="flex flex-col mb-8" data-testid="addTagsCreateIssue">
            <label className="text-sm pb-4 font-semibold" data-testid="addTagsTextLabel">
              <Text>{t("Add Tags")}</Text>
            </label>
            <MultiSelectComponentAny
              enabled
              dataSource={tagsDataSource}
              value={initialCustomTags}
              mode="Box"
              placeholder={t("Add a Tag")}
              allowCustomValue
              select={handleAddPreFilledTag}
              removed={handleRemoveCustomTag}
            />
          </div>

          {Object.keys(formTemplate).length > 0 && (
            <Accordion defaultExpanded className="w-full">
              <AccordionSummary>
                <div className="flex justify-between w-full py-3 items-center">
                  <Text className="text-xl font-semibold">{t("Custom Fields")}</Text>
                  <span>
                    <IconComponent name="arrowDown" width="1.25rem" height="1.25rem" className="cursor-pointer" />
                  </span>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="flex flex-col mb-8">
                  {!props.clashIssueData && (
                    <FormPreviewAny
                      formData={formTemplate}
                      formValidator={formValidator}
                      allowPadding={false}
                      allowBorder={false}
                      projectId={projectData._id}
                      disabled={false}
                      handleData={handleCustomFieldsData}
                      headingFontSize="text-lg"
                      fullWidthInput
                      removeIcons
                      resourceType={formCategories.issues}
                      templateSource={TEMPLATE_ENTITY_CREATION}
                    />
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          )}
        </div>

        <div className="flex justify-start gap-6 mt-6 px-6" data-testid="IssuesButton">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            // @ts-expect-error -- `({ clashDetails }?: { clashDetails?: ClashDetails })` is wired straight to `onClick`, so it destructures a MouseEvent. REAL BUG: `clashDetails` is always undefined on the click path. Fix is `onClick={() => handler()}` — a runtime change.
            onClick={handleCreateIssue}
            disabled={actionButtons || !spaceId}
          >
            {createButton}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleCancelIssue} disabled={actionButtons}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      </div>
      {showFileManager && (
        <FileManagerAsPopupAny
          projectId={projectData._id}
          handleFileSelectedButtonClick={handleFileSelectedButtonClick}
          handleFileSelectedCancelClick={handleFileCancelButtonClick}
          workspaceId={spaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideSchedules
          hideEstimates
          hideExternalSpace
          hideFilesWithoutVersion
          heading={t("Attach Files")}
          disableFiles={[]}
        />
      )}
      {props.triggerClashIssue && (
        <div className="fixed top-0 end-0 h-full w-full z-50 opacity-80 bg-black content-center text-center text-white">
          <Text>{t("Kindly do not close the tab")}</Text>
          <Text>{t("Please wait while creating clash issue")}</Text>
          {clashIssueProgress && (
            <>
              <Text>{`${clashIssueProgress.progress} ${t("out of")} ${clashIssueProgress.total} ${t(
                "issue created",
              )}`}</Text>
              <ButtonComponent
                onClick={() => {
                  setCancelClashIssue(true);
                }}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              >
                {cancelClashIssue ? t("Cancelling!") : t("Cancel")}
              </ButtonComponent>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default CreateIssue;
